import { apiCall } from './apiClient';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';
import { extractMrRouterResponse } from '../components/HomeChat/mrHandoff';
import type { MrChatSessionView } from '../components/MrChat/types';

export type MarketResearchNotificationContext = 'questions' | 'brand_name_ja' | 'product_discovery_review' | 'section_c_handoff';
export type WorkflowNotificationKind = MarketResearchNotificationContext | 'section_c_handoff_requested' | 'workflow_shared';
export type WorkflowNotificationStatus = 'unread' | 'read' | 'dismissed' | 'actioned' | 'completed' | 'superseded';

/** Deliberately limited to server-sanitized fields. */
export interface WorkflowNotification {
  notification_id: string;
  version: number;
  kind: WorkflowNotificationKind;
  status: WorkflowNotificationStatus;
  workflow_name: string;
  /** Server-selected stage code. The browser must redeem it before routing. */
  target_stage: number | 'ra_collaboration';
  created_at: string;
  read_at?: string | null;
  dismissed_at?: string | null;
  actioned_at?: string | null;
  /** False means the recipient may still dismiss the active notice, but its
   *  original target is no longer safe to open. */
  target_ready?: boolean;
  /** Server-authorized current-workflow fallback for a stale target. */
  can_open_workflow?: boolean;
  /** Server-issued only for a recipient-authorized MR chat attachment (RA
   * early-stage share or current CO/CS Section-C handoff). */
  can_continue_in_chat?: boolean;
  /** Server-issued only while the recipient may open a read-only live MR
   * projection. Absence fails closed; the browser never infers it from an
   * active lease. */
  can_view_in_chat?: boolean;
  /** Opaque-notification-only recovery for a durably interrupted MR run.
   * Kept in the sanitized transport contract for backwards compatibility;
   * notification UI never renders or invokes this action. */
  can_resume_in_chat?: boolean;
  /** Current canonical MR state only. The browser must never infer this from
   * the historical notification kind or target stage. */
  question_response_requested?: boolean;
  /** Identity-free server projection: a live MR execution temporarily owns
   * the canonical workflow, so no entry action may be rendered. */
  execution_active?: boolean;
  /** Exact accessible copy supplied only with `execution_active`. */
  active_work_message?: string;
}

export interface WorkflowNotificationListResult {
  notifications: WorkflowNotification[];
  has_more: boolean;
}

export interface MarketResearchNotificationRequestResult {
  created_count: number;
  already_pending: boolean;
  context: MarketResearchNotificationContext;
  no_counterpart?: boolean;
  /** Present only in the authenticated sender's action receipt, never inbox projections. */
  notified_users?: string[];
}

const notificationEndpointForContext = (context: MarketResearchNotificationContext): string => (
  context === 'questions'
    ? '/api/market-research/notify/customers'
    : context === 'brand_name_ja'
      ? '/api/market-research/notify/contracting'
      : context === 'section_c_handoff'
        ? '/api/market-research/notify/section-c-handoff'
      : '/api/market-research/notify/contracting-review'
);

const safeError = () => new Error(USER_FACING_ERROR_MESSAGES.wait_and_retry);

/** Payload-free invalidation; consumers reload their authorized projections. */
const signalInboxChanged = () => {
  window.dispatchEvent(new Event('workflow-notifications-changed'));
};

export async function requestMarketResearchNotification(
  flowId: string,
  context: MarketResearchNotificationContext,
  requestId: string,
): Promise<MarketResearchNotificationRequestResult> {
  const response = await apiCall<MarketResearchNotificationRequestResult>(
    notificationEndpointForContext(context),
    'POST',
    { flow_id: flowId, request_id: requestId },
  );
  if (!response.success || !response.data) throw safeError();
  return response.data;
}

/**
 * Commit an MR-only recovery share and its fixed-context notification together.
 * These exact addresses are a pending owner selection, not a recipient list:
 * the server reclassifies them, persists only eligible counterparts, and then
 * resolves the notification from the workflow's metadata scope.
 */
export async function shareAndNotifyMarketResearchCounterparts(
  flowId: string,
  context: MarketResearchNotificationContext,
  emails: string[],
  requestId: string,
): Promise<MarketResearchNotificationRequestResult> {
  const response = await apiCall<MarketResearchNotificationRequestResult>(
    '/api/market-research/notify/share-and-notify',
    'POST',
    { flow_id: flowId, context, emails, request_id: requestId },
  );
  if (!response.success || !response.data) throw safeError();
  return response.data;
}

export async function fetchWorkflowNotificationUnreadCount(): Promise<number> {
  const response = await apiCall<{ unread_count?: number }>('/api/workflow-notifications/unread-count', 'GET');
  if (!response.success || !response.data || !Number.isSafeInteger(response.data.unread_count) || response.data.unread_count! < 0) {
    return 0;
  }
  return response.data.unread_count!;
}

export async function listWorkflowNotifications(
  offset = 0,
  includeTerminal = false,
): Promise<WorkflowNotificationListResult> {
  const params = new URLSearchParams({ limit: '50', offset: String(Math.max(0, offset)), include_terminal: String(includeTerminal) });
  const suffix = `?${params.toString()}`;
  const response = await apiCall<WorkflowNotificationListResult>(`/api/workflow-notifications${suffix}`, 'GET');
  if (!response.success || !response.data || !Array.isArray(response.data.notifications)) throw safeError();
  return { notifications: response.data.notifications, has_more: response.data.has_more === true };
}

export async function listFlowWorkflowNotifications(flowId: string): Promise<WorkflowNotificationListResult> {
  const response = await apiCall<WorkflowNotificationListResult>(
    `/api/workflow-notifications/flows/${encodeURIComponent(flowId)}`,
    'GET',
  );
  if (!response.success || !response.data || !Array.isArray(response.data.notifications)) throw safeError();
  return { notifications: response.data.notifications, has_more: response.data.has_more === true };
}

/** Server-authorized counts for flows already rendered to this signed-in user. */
export async function fetchWorkflowNotificationCounts(
  flowIds: string[], signal?: AbortSignal,
): Promise<Record<string, number>> {
  // Keep this in lockstep with the server's access-checked batch cap. Chunk
  // rather than truncate so a long workflow list cannot silently lose dots.
  const ids = Array.from(new Set(flowIds.filter((flowId) => typeof flowId === 'string' && flowId.length > 0)));
  if (ids.length === 0) return {};
  const countChunks = await Promise.all(Array.from(
    { length: Math.ceil(ids.length / 100) },
    async (_, index) => {
      const query = ids.slice(index * 100, (index + 1) * 100)
        .map((flowId) => `flow_ids=${encodeURIComponent(flowId)}`)
        .join('&');
      const response = await apiCall<{ counts?: Record<string, unknown> }>(
        `/api/workflow-notifications/flow-counts?${query}`,
        'GET',
        undefined,
        { signal, timeoutMs: 15_000, retries: 0 },
      );
      const counts = response.data?.counts;
      if (!response.success || !counts || typeof counts !== 'object'
        || Array.isArray(counts)
        || Object.values(counts).some((count) => (
          !Number.isSafeInteger(count) || (count as number) < 0
        ))) throw safeError();
      return Object.fromEntries(Object.entries(counts).flatMap(([flowId, count]) => (
        Number.isSafeInteger(count) && (count as number) > 0 ? [[flowId, count as number]] : []
      )));
    },
  ));
  return Object.assign({}, ...countChunks);
}

async function mutateNotification(
  notificationId: string,
  action: 'mark-read' | 'dismiss',
  version: number,
): Promise<WorkflowNotification> {
  const response = await apiCall<WorkflowNotification>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/${action === 'mark-read' ? 'read' : action}`,
    'POST',
    { version },
  );
  if (!response.success || !response.data) throw safeError();
  signalInboxChanged();
  return response.data;
}

export const markWorkflowNotificationRead = (notificationId: string, version: number) => (
  mutateNotification(notificationId, 'mark-read', version)
);

export const dismissWorkflowNotification = (notificationId: string, version: number) => (
  mutateNotification(notificationId, 'dismiss', version)
);

export interface RedeemedWorkflowNotificationTarget {
  /** Returned only after the server validates this recipient-bound request. */
  flowId: string;
  stageCode: number | 'ra_collaboration';
  /** Stale-target fallback opens the already-authorized actual workflow. */
  navigationMode?: 'current_workflow';
}

/** Minimal, allowlisted recipient projection returned by Questions handoff. */
export interface ContinuedQuestionsChat {
  homeChatSessionId: string;
  mrChat: MrChatSessionView;
  /** Opaque recipient notification id used solely for the server-authorized
   * observer progress endpoint. It is never a flow/stage selector. */
  observerNotificationId?: string;
  /** A CE-safe, notification-bound controller promotion; never a general
   * Home Chat entitlement. */
  notificationBoundResume?: boolean;
  notificationBoundResumeId?: string;
}

type ChatAttachmentMode = 'continue' | 'observer';

/** Parse one minimal recipient-bound chat attachment. The server owns both
 * the Home Chat binding and controller state; neither is browser-derived. */
function parseChatAttachment(
  response: Awaited<ReturnType<typeof apiCall<{ home_chat_session_id?: unknown; mr_chat?: unknown }>>>,
  mode: ChatAttachmentMode,
): ContinuedQuestionsChat {
  if (!response.success) {
    // Preserve the small allowlisted lifecycle code so the notification panel
    // can reload a server-authoritative active-work row instead of rendering
    // its generic "Unable to open" copy for an expected lease/marker race.
    const error = safeError() as Error & { error_code?: string };
    if (typeof response.error_code === 'string') error.error_code = response.error_code;
    throw error;
  }
  const data = response.data;
  const mrChat = extractMrRouterResponse({
    status: 'mr_chat',
    mr_chat: data?.mr_chat,
    controller: data && typeof data === 'object' ? (data as { controller?: unknown }).controller : undefined,
    home_chat_session_id: data && typeof data === 'object' ? (data as { home_chat_session_id?: unknown }).home_chat_session_id : undefined,
  });
  const hasCanonicalPendingForm = Boolean(
    mrChat?.pending_input && Array.isArray(mrChat.pending_input.fields),
  );
  const isWaitingForExecution = (
    mrChat?.controller?.state === 'waiting_for_execution'
    && mrChat.pending_input === null
  );
  const isObserverRunning = (
    mrChat?.controller?.state === 'observer_running'
    && mrChat.pending_input === null
  );
  const isRunning = (
    mrChat?.controller?.state === 'running'
    && mrChat.pending_input === null
    && mrChat.progress?.status === 'in_progress'
    && typeof mrChat.progress?.current_operation === 'object'
    && mrChat.progress.current_operation !== null
  );
  const isFinalizedSectionCTerminal = (
    mrChat?.controller?.state === 'read_only'
    && mrChat.workflow?.status === 'section_c_handoff_required'
    && mrChat.pending_input === null
  );
  // A receipt-bound CO/CS chat may end at supported Stage 12 when DD/DAF
  // 2579 is not applicable. This is a server-issued terminal projection,
  // not a browser-side completion inference; Stage 13 uses the typed Open
  // Workflow fallback and never reaches this parser.
  const isSupportedDownstreamTerminal = (
    mrChat?.controller?.state === 'ready'
    && mrChat.workflow?.status === 'complete'
    && mrChat.cursor === 12
    && mrChat.pending_input === null
  );
  const permittedShape = mode === 'observer'
    ? isObserverRunning
    : hasCanonicalPendingForm || isWaitingForExecution || isRunning || isFinalizedSectionCTerminal || isSupportedDownstreamTerminal;
  if (
    !data || typeof data.home_chat_session_id !== 'string' || !data.home_chat_session_id
    || !mrChat || typeof mrChat.workflow?.flow_id !== 'string'
    || !permittedShape
  ) throw safeError();
  return { homeChatSessionId: data.home_chat_session_id, mrChat };
}

export async function continueQuestionsNotificationInChat(
  notificationId: string,
  clientRequestId: string,
): Promise<ContinuedQuestionsChat> {
  const response = await apiCall<{ home_chat_session_id?: unknown; mr_chat?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/continue-in-chat`,
    'POST',
    { client_request_id: clientRequestId },
  );
  return parseChatAttachment(response, 'continue');
}

/** Attach a recipient as a read-only observer of an already-running MR
 * operation. This is intentionally distinct from Continue: it accepts only
 * the server's explicit `observer_running` projection. */
export async function viewWorkflowNotificationInChat(
  notificationId: string,
  clientRequestId: string,
): Promise<ContinuedQuestionsChat> {
  const response = await apiCall<{ home_chat_session_id?: unknown; mr_chat?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/view-in-chat`,
    'POST',
    { client_request_id: clientRequestId },
  );
  return { ...parseChatAttachment(response, 'observer'), observerNotificationId: notificationId };
}

/** Resume a server-marked interruption from this recipient's opaque notice.
 * This is deliberately distinct from ordinary Home Chat turns so a CE does
 * not gain a general chat route merely by being shared one workflow. */
export async function resumeWorkflowNotificationInChat(
  notificationId: string,
  clientRequestId: string,
): Promise<ContinuedQuestionsChat> {
  const response = await apiCall<{ home_chat_session_id?: unknown; mr_chat?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/resume-in-chat`,
    'POST',
    { client_request_id: clientRequestId },
  );
  return {
    ...parseChatAttachment(response, 'continue'),
    notificationBoundResume: true,
    notificationBoundResumeId: notificationId,
  };
}

/** Submit an allowed controller turn from a promoted notification binding.
 * This does not use the general Home Chat turn endpoint. */
export async function postWorkflowNotificationResumeTurn(
  notificationId: string,
  input: { client_request_id: string; input?: Record<string, unknown>; action?: string },
): Promise<MrChatSessionView> {
  const response = await apiCall<{ mr_chat?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/resume-in-chat/turn`,
    'POST', input,
  );
  const mrChat = extractMrRouterResponse({ status: 'mr_chat', mr_chat: response.data?.mr_chat });
  if (!response.success || !mrChat || typeof mrChat.workflow?.flow_id !== 'string') throw safeError();
  return mrChat;
}

/** Poll a promoted notification-bound controller while repeating its ACL. */
export async function getWorkflowNotificationResumeProgress(
  notificationId: string,
): Promise<MrChatSessionView> {
  const response = await apiCall<{ mr_chat?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/resume-in-chat/progress`,
    'GET',
  );
  const mrChat = extractMrRouterResponse({ status: 'mr_chat', mr_chat: response.data?.mr_chat });
  if (!response.success || !mrChat || typeof mrChat.workflow?.flow_id !== 'string') throw safeError();
  return mrChat;
}

/** Refresh the read-only live projection attached from an active notice.
 * The notification id is authorization-bound server-side; the UI supplies no
 * workflow id, stage, or controller action. */
export async function getWorkflowNotificationObserverProgress(
  notificationId: string,
): Promise<MrChatSessionView> {
  const response = await apiCall<{ mr_chat?: unknown; controller?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/view-in-chat/progress`,
    'GET',
  );
  const data = response.data;
  const mrChat = extractMrRouterResponse({
    status: 'mr_chat',
    mr_chat: data?.mr_chat,
    controller: data?.controller,
  });
  const hasCanonicalPendingForm = Boolean(
    mrChat?.pending_input && Array.isArray(mrChat.pending_input.fields),
  );
  const isObserverRunning = (
    mrChat?.controller?.state === 'observer_running'
    && mrChat.pending_input === null
  );
  const isObserverIdle = (
    mrChat?.controller?.state === 'idle_readonly'
    && mrChat.pending_input === null
  );
  if (
    !response.success || !mrChat || typeof mrChat.workflow?.flow_id !== 'string'
    || (!isObserverRunning && !isObserverIdle && !hasCanonicalPendingForm)
  ) throw safeError();
  return mrChat;
}

/**
 * Resolve the recipient-bound target without mutating the inbox record. The
 * browser never derives a flow or stage from the notification projection.
 */
export async function resolveWorkflowNotificationTarget(
  notificationId: string,
): Promise<RedeemedWorkflowNotificationTarget> {
  const response = await apiCall<{ flow_id?: string; target_stage?: number | string }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/target`,
    'GET',
  );
  const navigation = response.data;
  if (!response.success || !navigation || typeof navigation.flow_id !== 'string'
    || !(Number.isSafeInteger(navigation.target_stage) || navigation.target_stage === 'ra_collaboration')) {
    throw safeError();
  }
  return { flowId: navigation.flow_id, stageCode: navigation.target_stage as number | 'ra_collaboration' };
}

/** Resolve a safe current-workflow fallback without redeeming the stale notice. */
export async function resolveWorkflowNotificationFallbackTarget(
  notificationId: string,
): Promise<RedeemedWorkflowNotificationTarget> {
  const response = await apiCall<{ flow_id?: string; target_stage?: number | string; navigation_mode?: unknown }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/workflow-target`,
    'GET',
  );
  const navigation = response.data;
  if (!response.success || !navigation || typeof navigation.flow_id !== 'string'
    || !(Number.isSafeInteger(navigation.target_stage) || navigation.target_stage === 'ra_collaboration')) {
    throw safeError();
  }
  if (navigation.navigation_mode !== 'current_workflow') throw safeError();
  return {
    flowId: navigation.flow_id,
    stageCode: navigation.target_stage as number | 'ra_collaboration',
    navigationMode: 'current_workflow',
  };
}

/**
 * Terminalize a notification only after its authorized target has loaded.
 * The server keeps this POST idempotent for safe client retries.
 */
export async function redeemWorkflowNotification(
  notificationId: string,
  _version: number,
): Promise<RedeemedWorkflowNotificationTarget> {
  const response = await apiCall<{ flow_id?: string; target_stage?: number | string }>(
    `/api/workflow-notifications/${encodeURIComponent(notificationId)}/target`,
    'POST',
  );
  const navigation = response.data;
  if (!response.success || !navigation || typeof navigation.flow_id !== 'string'
    || !(Number.isSafeInteger(navigation.target_stage) || navigation.target_stage === 'ra_collaboration')) {
    throw safeError();
  }
  signalInboxChanged();
  return { flowId: navigation.flow_id, stageCode: navigation.target_stage as number | 'ra_collaboration' };
}
